import { cx } from "$src/lib/utils";
import type { Placement } from "@floating-ui/react-dom";
import { Tooltip, type TooltipProps } from "@gotracksuit/design";
import type { LucideIcon } from "lucide-react";
import React, { ComponentProps, forwardRef } from "react";

import styles from "./icon-button.module.css";

export type IconButtonProps = {
  /** Icon for the button */
  icon: LucideIcon;
  /** Optional active state */
  active?: boolean;
  /** Optional large version */
  large?: boolean;
  /** Optional small version */
  small?: boolean;
  /** Optional disabled state */
  disabled?: boolean;
  /** Optional hidden state */
  hidden?: boolean;
  /** Optional tooltip title */
  title?: string;
  /** Optional tooltip */
  tip?: string;
  /** Optional tip placement */
  tipPlacement?: Placement;
  /** Optional button props */
  buttonProps?: React.ButtonHTMLAttributes<HTMLButtonElement>;
  /** Optional icon props */
  iconProps?: ComponentProps<LucideIcon>;
} & Partial<TooltipProps>;

/**
 * @component
 * Simple button with icon
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon: Icon,
      active = false,
      large = false,
      small = false,
      disabled = false,
      hidden = false,
      title,
      tip,
      tipPlacement,
      buttonProps,
      className,
      iconProps,
      ...props
    },
    ref,
  ) => (
    <Tooltip
      className={cx(className, disabled && styles.disabled)}
      placement={tipPlacement}
      disabled={tip === undefined}
      title={title}
      tip={tip}
      {...props}
    >
      <button
        className={cx(
          styles.button,
          active && styles.active,
          large && styles.large,
          small && styles.small,

          hidden && styles.hidden,
        )}
        ref={ref}
        data-testid="icon-button"
        {...buttonProps}
      >
        <Icon
          data-testid="icon"
          className={styles.icon}
          size={20}
          {...iconProps}
        />
      </button>
    </Tooltip>
  ),
);
